/**
 * Заставка TIPADUMA: «нажмите, чтобы начать» → 16 секунд грозы над
 * особняком → логотип → меню.
 *
 * Показывается при первом заходе (флаг в localStorage) и по кнопке
 * «Заставка» в меню. Пропуск — клик, тап или любая клавиша.
 * ?intro=1 — показать принудительно, ?intro=0 — никогда,
 * ?introDebug=1 — режим покадровой съёмки (tools/intro/capture.mjs).
 */
import { IntroScene } from "./scene";
import { IntroSound, renderSoundtrack } from "./sound";
import { CUE } from "./timeline";
import { t } from "../i18n";

export { warmHttpCache, warmLevel } from "./prefetch";

const LS_SEEN = "tipaduma.intro.seen";

export interface IntroOptions {
  /** Кнопка «Заставка» в меню — повторный показ. */
  menuButton?: HTMLElement | null;
  /** Первый жест пользователя: разблокировать звук игры (музыка меню). */
  onGesture?: () => void;
  /** Заставка начала растворяться — пора включать музыку меню. */
  onReveal?: () => void;
  /** Заставка закрыта полностью. */
  onFinish?: () => void;
  /** Заставка пошла — можно качать уровень в фоне. */
  prefetch?: () => void;
}

type State = "off" | "gate" | "loading" | "play" | "closing";

let state: State = "off";
let opts: IntroOptions = {};
let root: HTMLDivElement | null = null;
let scene: IntroScene | null = null;
let sound: IntroSound | null = null;
let ready: Promise<boolean> | null = null;
let raf = 0;
let perfStart = 0;
let audioClock = false;
let fadeStarted = false;
let watchdog = 0;
let reducedMotion = false;
const el: Record<string, HTMLElement> = {};

/** Пока заставка на экране (до начала затухания), музыка меню молчит. */
export function introBlocksMusic(): boolean {
  return state === "gate" || state === "loading" || state === "play";
}

export function introActive(): boolean {
  return state !== "off";
}

/** Колбэки игры не должны ронять заставку (и оставлять игрока на чёрном экране). */
function safe(fn: (() => void) | undefined): void {
  try {
    fn?.();
  } catch (e) {
    console.warn("[intro] колбэк игры упал:", e);
  }
}

export function initIntro(o: IntroOptions): void {
  opts = o;
  injectStyle();
  reducedMotion = !!window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
  o.menuButton?.addEventListener("click", (ev) => {
    ev.preventDefault();
    replay();
  });
  const q = new URLSearchParams(location.search);
  if (q.get("introDebug") === "1") {
    setupDebug();
    return;
  }
  const force = q.get("intro") === "1";
  const disabled = q.get("intro") === "0";
  let seen = false;
  try {
    seen = localStorage.getItem(LS_SEEN) === "1";
  } catch {
    /* приватный режим — считаем, что не видел */
  }
  const bot = navigator.webdriver === true;
  if (force || (!disabled && !seen && !bot)) openGate();
  else clearPending();
}

function clearPending(): void {
  document.documentElement.classList.remove("intro-pending");
}

// ─── DOM ──────────────────────────────────────────────────────────────────

function build(withGate: boolean): void {
  root?.remove();
  const touch = !!window.matchMedia?.("(pointer: coarse)").matches;
  const r = document.createElement("div");
  r.id = "intro-root";
  r.setAttribute("role", "presentation");
  r.innerHTML = `
    <div class="intro__presents" aria-hidden="true">WOGUL GAMES<small>${t("представляет")}</small></div>
    <div class="intro__gate${withGate ? "" : " intro--hidden"}">
      <div class="intro__tap">${touch ? t("КОСНИТЕСЬ, ЧТОБЫ НАЧАТЬ") : t("НАЖМИТЕ, ЧТОБЫ НАЧАТЬ")}</div>
      <div class="intro__sub">${t("включите звук")}</div>
    </div>
    <div class="intro__loading intro--hidden">${t("загрузка…")}</div>
    <div class="intro__skip">${touch ? t("коснитесь — пропустить") : t("любая клавиша — пропустить")}</div>`;
  scene = new IntroScene();
  scene.canvas.className = "intro__canvas";
  r.prepend(scene.canvas);
  document.body.appendChild(r);
  root = r;
  for (const k of ["presents", "gate", "loading", "skip"]) el[k] = r.querySelector(`.intro__${k}`) as HTMLElement;
  // Экран «нажмите» ждёт click: на iOS звук будится только в click/touchend,
  // pointerdown там приходит раньше и жестом для Web Audio не считается.
  r.addEventListener("click", onClick);
  r.addEventListener("pointerdown", onPointer);
  window.addEventListener("keydown", onKey, { capture: true });
  document.addEventListener("visibilitychange", onVisibility);
  clearPending();
}

function teardown(): void {
  cancelAnimationFrame(raf);
  raf = 0;
  clearTimeout(watchdog);
  window.removeEventListener("keydown", onKey, { capture: true });
  document.removeEventListener("visibilitychange", onVisibility);
  root?.remove();
  root = null;
  scene = null;
  // звук гасим мягко и закрываем чуть позже — без щелчка на обрыве
  const snd = sound;
  snd?.fadeOut(0.25);
  window.setTimeout(() => snd?.close(), 400);
  sound = null;
  ready = null;
}

// ─── ход событий ──────────────────────────────────────────────────────────

function loadAll(): Promise<boolean> {
  if (!scene || !sound) return Promise.resolve(false);
  const sc = scene;
  sound.prefetch();
  return sc
    .load()
    .then(() => true)
    .catch((e) => {
      console.warn("[intro] ассеты не загрузились, заставка пропущена:", e);
      return false;
    });
}

function openGate(): void {
  if (state !== "off") return;
  build(true);
  sound = new IntroSound();
  state = "gate";
  ready = loadAll();
  const idle = (now: number): void => {
    if (state !== "gate" && state !== "loading") return;
    if (scene && ready) {
      scene.resize(innerWidth, innerHeight);
      scene.drawIdle(now / 1000);
    }
    raf = requestAnimationFrame(idle);
  };
  raf = requestAnimationFrame(idle);
}

function replay(): void {
  if (state !== "off") return;
  build(false);
  sound = new IntroSound();
  // клик по кнопке меню — это жест: звук можно будить сразу
  sound.unlock();
  state = "loading";
  ready = loadAll();
  void proceed();
}

async function proceed(): Promise<void> {
  state = "loading";
  const loadingTimer = window.setTimeout(() => el.loading?.classList.remove("intro--hidden"), 250);
  el.gate?.classList.add("intro--hidden");
  // медленная сеть: не держим игрока дольше 8 с — сразу в меню
  const ok = await Promise.race([
    ready ?? Promise.resolve(false),
    new Promise<boolean>((res) => setTimeout(() => res(false), 8000)),
  ]);
  clearTimeout(loadingTimer);
  if (state !== "loading") return;
  if (!ok) {
    finish();
    return;
  }
  const hasSound = sound ? await sound.prepare() : false;
  if (state !== "loading") return;
  el.loading?.classList.add("intro--hidden");
  begin(hasSound);
}

function begin(hasSound: boolean): void {
  try {
    localStorage.setItem(LS_SEEN, "1");
  } catch {
    /* ignore */
  }
  audioClock = hasSound && !!sound?.start() && !!sound?.running;
  perfStart = performance.now() + 60;
  fadeStarted = false;
  state = "play";
  // уровень — в фон, пока смотрят заставку
  window.setTimeout(() => safe(opts.prefetch), 400);
  // страховка: что бы ни случилось с кадрами, меню вернётся
  watchdog = window.setTimeout(() => skip(), (CUE.end + 6) * 1000);
  cancelAnimationFrame(raf);
  raf = requestAnimationFrame(tick);
}

function clock(): number {
  if (audioClock && sound?.running) return Math.max(0, sound.now());
  return Math.max(0, (performance.now() - perfStart) / 1000);
}

function tick(): void {
  if (state !== "play" && state !== "closing") return;
  const t = clock();
  if (scene) {
    scene.resize(innerWidth, innerHeight);
    scene.reducedMotion = reducedMotion;
    scene.draw(t);
  }
  updateText(t);
  if (t >= CUE.fadeOutStart && !fadeStarted) startFade(CUE.end - CUE.fadeOutStart);
  if (t >= CUE.end) {
    finish();
    return;
  }
  raf = requestAnimationFrame(tick);
}

function updateText(t: number): void {
  const p = el.presents;
  if (p) {
    const a = Math.min(1, Math.max(0, (t - CUE.presentsIn) / 0.6)) * Math.min(1, Math.max(0, (CUE.presentsOut - t) / 0.6));
    p.style.opacity = a.toFixed(3);
  }
  el.skip?.classList.toggle("intro__skip--on", t > 1.2 && t < CUE.fadeOutStart);
}

function startFade(sec: number): void {
  fadeStarted = true;
  state = "closing";
  if (root) {
    root.style.transition = `opacity ${sec.toFixed(2)}s ease-in`;
    root.style.opacity = "0";
    root.style.pointerEvents = "none";
  }
  safe(opts.onReveal);
}

/** Пропуск: быстрое затухание звука и картинки. */
function skip(): void {
  if (state === "off" || state === "closing") return;
  if (state === "gate") {
    finish();
    return;
  }
  sound?.fadeOut(0.4);
  startFade(0.45);
  // кадры идут дальше (дождь не замирает), картинка гаснет CSS-переходом
  window.setTimeout(finish, 480);
}

function finish(): void {
  if (state === "off") return;
  const wasGate = state === "gate" || state === "loading";
  teardown();
  state = "off";
  if (wasGate) safe(opts.onReveal);
  safe(opts.onFinish);
}

// ─── ввод ─────────────────────────────────────────────────────────────────

function onGesture(): void {
  if (state === "gate") {
    sound?.unlock();
    safe(opts.onGesture);
    void proceed();
    return;
  }
  // первые полсекунды не пропускаем: тот же тап мог «дребезжать»
  if (state === "play" && clock() > 0.5) skip();
}

function onClick(ev: MouseEvent): void {
  ev.preventDefault();
  if (state === "gate") onGesture();
}

function onPointer(ev: PointerEvent): void {
  // пропуск — сразу по касанию, не дожидаясь отпускания
  if (state === "play") {
    ev.preventDefault();
    onGesture();
  }
}

function onKey(ev: KeyboardEvent): void {
  if (state === "off") return;
  // заставка на экране — клавиши не должны доходить до игры
  ev.stopImmediatePropagation();
  if (ev.repeat) return;
  if (["Shift", "Control", "Alt", "Meta", "CapsLock"].includes(ev.key)) return;
  ev.preventDefault();
  onGesture();
}

function onVisibility(): void {
  // вкладку спрятали посреди заставки — смотреть уже некому, сразу в меню
  if (document.hidden && (state === "play" || state === "loading")) {
    sound?.fadeOut(0.05);
    finish();
  }
}

// ─── стили ────────────────────────────────────────────────────────────────

function injectStyle(): void {
  if (document.getElementById("intro-style")) return;
  const s = document.createElement("style");
  s.id = "intro-style";
  s.textContent = `
#intro-root{position:fixed;inset:0;z-index:30000;background:#000;overflow:hidden;touch-action:none;
  cursor:pointer;-webkit-user-select:none;user-select:none;font-family:var(--font-game,"VT323",monospace)}
#intro-root .intro__canvas{position:absolute;inset:0;width:100%;height:100%;display:block;
  image-rendering:crisp-edges;image-rendering:pixelated}
#intro-root .intro--hidden{display:none!important}
.intro__gate{position:absolute;left:0;right:0;bottom:24%;text-align:center;pointer-events:none;padding:0 16px}
.intro__tap{color:#d64433;font-size:clamp(22px,4.4vw,42px);letter-spacing:.16em;
  text-shadow:0 0 14px rgba(214,68,51,.55),0 2px 0 #000;animation:introPulse 1.7s ease-in-out infinite}
.intro__sub{margin-top:10px;color:#7d8199;font-size:clamp(15px,2.3vw,21px);letter-spacing:.14em}
.intro__loading{position:absolute;left:0;right:0;bottom:24%;text-align:center;color:#7d8199;
  font-size:clamp(16px,2.4vw,22px);letter-spacing:.2em;pointer-events:none}
.intro__presents{position:absolute;left:0;right:0;bottom:15%;text-align:center;color:#c9cbe0;opacity:0;
  font-size:clamp(22px,4vw,40px);letter-spacing:.38em;text-shadow:0 2px 0 #000,0 0 18px rgba(0,0,0,.9);pointer-events:none;padding:0 16px}
.intro__presents small{display:block;margin-top:4px;font-size:.52em;letter-spacing:.55em;color:#8d90a8}
.intro__skip{position:absolute;right:max(16px,env(safe-area-inset-right));bottom:max(14px,env(safe-area-inset-bottom));
  color:#8d90a8;font-size:16px;letter-spacing:.12em;opacity:0;transition:opacity .6s;pointer-events:none}
.intro__skip--on{opacity:.55}
@keyframes introPulse{0%,100%{opacity:.4}50%{opacity:1}}
html.intro-pending #overlay{visibility:hidden}`;
  document.head.appendChild(s);
}

// ─── режим съёмки ─────────────────────────────────────────────────────────

function setupDebug(): void {
  build(false);
  el.skip?.classList.add("intro--hidden");
  state = "closing"; // не блокируем музыку меню и не ловим клавиши
  window.removeEventListener("keydown", onKey, { capture: true });
  const sc = scene as IntroScene;
  const api = {
    ready: sc.load().then(() => true),
    renderAt(t: number): void {
      sc.resize(innerWidth, innerHeight);
      sc.draw(t);
      updateText(t);
    },
    renderIdle(t: number): void {
      sc.resize(innerWidth, innerHeight);
      sc.drawIdle(t);
      el.gate?.classList.remove("intro--hidden");
    },
    info: () => sc.debugInfo(),
    async soundtrackWav(only?: "music" | "amb" | "thunder" | "fx"): Promise<string> {
      const buf = await renderSoundtrack(44100, only);
      return wavBase64(buf);
    },
  };
  (window as unknown as { __introDebug: typeof api }).__introDebug = api;
}

function wavBase64(buf: AudioBuffer): string {
  const ch = buf.numberOfChannels;
  const n = buf.length;
  const bytes = new Uint8Array(44 + n * ch * 2);
  const dv = new DataView(bytes.buffer);
  const w = (o: number, s: string): void => {
    for (let i = 0; i < s.length; i++) bytes[o + i] = s.charCodeAt(i);
  };
  w(0, "RIFF");
  dv.setUint32(4, 36 + n * ch * 2, true);
  w(8, "WAVEfmt ");
  dv.setUint32(16, 16, true);
  dv.setUint16(20, 1, true);
  dv.setUint16(22, ch, true);
  dv.setUint32(24, buf.sampleRate, true);
  dv.setUint32(28, buf.sampleRate * ch * 2, true);
  dv.setUint16(32, ch * 2, true);
  dv.setUint16(34, 16, true);
  w(36, "data");
  dv.setUint32(40, n * ch * 2, true);
  const data = Array.from({ length: ch }, (_, c) => buf.getChannelData(c));
  let o = 44;
  for (let i = 0; i < n; i++) {
    for (let c = 0; c < ch; c++) {
      const v = Math.max(-1, Math.min(1, data[c][i]));
      dv.setInt16(o, v < 0 ? v * 0x8000 : v * 0x7fff, true);
      o += 2;
    }
  }
  let s = "";
  const CH = 0x8000;
  for (let i = 0; i < bytes.length; i += CH) s += String.fromCharCode(...bytes.subarray(i, i + CH));
  return btoa(s);
}
